// Tracking → Exceptions tab.
//
// Orders that hit a canonical_type='exception' event at any point in
// the window. Same row → drawer interaction as In-flight, plus a
// breakdown sidebar showing exception_type × carrier counts so
// operators can spot patterns ("FedEx address unknowns are up this
// week").
//
// We don't yet have a resolved/resolution-note column on
// tracking_events — Phase-2 work. For now this is a read-only triage
// list.

import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  Loader2, Search, X, RefreshCw, OctagonAlert, ChevronLeft, ChevronRight, MapPin,
} from 'lucide-react'
import { format, parseISO, formatDistanceToNow } from 'date-fns'
import { getTrackingExceptions } from '../../lib/api'
import OrderDetailDrawer from './OrderDetailDrawer'

// Same carrier groups as TrackingInFlightTab — see comment there.
const CARRIER_GROUPS = [
  { key: 'australia_post', label: 'Australia Post',
    codes: ['australia_post', 'australia_post_mypost_business'] },
  { key: 'fedex',          label: 'FedEx',
    codes: ['fedex'] },
  { key: 'dhl',            label: 'DHL',
    codes: ['dhl_ecommerce_au', 'dhl_express_australia'] },
  { key: 'ups',            label: 'UPS',
    codes: ['ups'] },
  // TNT omitted — no tracking integration.
]
const STATE_OPTIONS = ['VIC', 'NSW', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']

function carrierGroupOn(filters, group) {
  return group.codes.every(c => filters.carrier.includes(c))
}
function toggleCarrierGroup(filters, setFilter, group) {
  const set = new Set(filters.carrier)
  const allOn = carrierGroupOn(filters, group)
  if (allOn) group.codes.forEach(c => set.delete(c))
  else       group.codes.forEach(c => set.add(c))
  setFilter({ carrier: [...set] })
}

function ChipGroup({ label, options, values, onChange }) {
  const set = new Set(values || [])
  const toggle = v => {
    const next = new Set(set)
    if (next.has(v)) next.delete(v); else next.add(v)
    onChange([...next])
  }
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</p>
      <div className="flex flex-wrap gap-1">
        {options.map(opt => {
          const value = typeof opt === 'string' ? opt : opt.value
          const text  = typeof opt === 'string' ? opt : opt.label
          const on    = set.has(value)
          return (
            <button
              key={value}
              onClick={() => toggle(value)}
              className={[
                'text-xs px-2 py-0.5 rounded border transition-colors',
                on ? 'bg-red-600 border-red-600 text-white'
                   : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
              ].join(' ')}
            >
              {text}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function timeAgo(iso) {
  if (!iso) return '—'
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }) } catch { return '—' }
}

function fmtDate(iso) {
  if (!iso) return '—'
  try { return format(parseISO(iso), 'd MMM') } catch { return '—' }
}

function useFilterParams() {
  const [params, setParams] = useSearchParams()
  const get = key => params.getAll(key)
  const intl = params.get('international')
  // Destination defaults to Domestic — see TrackingInFlightTab for the
  // full rationale + URL encoding.
  const destSel = intl === 'all' ? 'all' : intl === 'true' ? 'intl' : 'domestic'
  const filters = {
    carrier:       get('carrier'),
    state:         get('state'),
    international: intl === 'all' ? undefined : intl === 'true' ? true : false,
    destSel,
    // Owned by the Tracking page shell — same global filter as the
    // other tabs. See TrackingInFlightTab for the same pattern.
    client_id:     params.get('client_id') || undefined,
    search:        params.get('search') || '',
    days:          Number(params.get('days') || 30),
    page:          Number(params.get('page') || 1),
  }
  function setFilter(patch) {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      next.delete(k)
      if (Array.isArray(v)) v.forEach(x => x != null && next.append(k, String(x)))
      else if (v != null && v !== '') next.set(k, String(v))
    }
    if (!('page' in patch)) next.delete('page')
    setParams(next, { replace: true })
  }
  return [filters, setFilter]
}

export default function TrackingExceptionsTab() {
  const qc = useQueryClient()
  const [filters, setFilter] = useFilterParams()
  const [drawerRow, setDrawerRow] = useState(null)
  const [searchInput, setSearchInput] = useState(filters.search)

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['tracking', 'exceptions', filters],
    queryFn: () => getTrackingExceptions({
      days:          filters.days,
      carrier:       filters.carrier,
      state:         filters.state,
      international: filters.international,
      client_id:     filters.client_id,
      search:        filters.search || undefined,
      page:          filters.page,
      page_size:     50,
    }),
    refetchInterval: 60_000,
    keepPreviousData: true,
  })

  const rows      = data?.rows      || []
  const total     = data?.total     || 0
  const breakdown = data?.breakdown || []
  const pageSize  = data?.page_size || 50
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  const activeFilterCount = useMemo(() => (
    (filters.carrier.length > 0 ? 1 : 0) +
    (filters.state.length > 0 ? 1 : 0) +
    (filters.destSel !== 'domestic' ? 1 : 0) +
    (filters.search ? 1 : 0)
  ), [filters])

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
      {/* Main column */}
      <div>
        {/* Filter bar */}
        <div className="card p-4 mb-4 space-y-3">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            {/* Carrier groups — same shape + behaviour as InFlightTab. */}
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Carrier</p>
              <div className="flex flex-wrap gap-1">
                {CARRIER_GROUPS.map(g => {
                  const on = carrierGroupOn(filters, g)
                  return (
                    <button
                      key={g.key}
                      onClick={() => toggleCarrierGroup(filters, setFilter, g)}
                      className={[
                        'text-xs px-2 py-0.5 rounded border transition-colors',
                        on ? 'bg-red-600 border-red-600 text-white'
                           : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
                      ].join(' ')}
                    >
                      {g.label}
                    </button>
                  )
                })}
              </div>
            </div>
            <ChipGroup
              label="Destination state"
              options={STATE_OPTIONS}
              values={filters.state}
              onChange={v => setFilter({ state: v })}
            />
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Destination</p>
              <div className="inline-flex rounded border border-slate-200 overflow-hidden text-xs bg-white">
                {[
                  { sel: 'domestic', label: 'Domestic',      set: undefined },
                  { sel: 'all',      label: 'All',           set: 'all'     },
                  { sel: 'intl',     label: 'International',  set: 'true'    },
                ].map(opt => (
                  <button
                    key={opt.sel}
                    onClick={() => setFilter({ international: opt.set })}
                    className={[
                      'px-2.5 py-0.5 font-medium transition-colors',
                      filters.destSel === opt.sel
                        ? 'bg-red-600 text-white'
                        : 'text-slate-600 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Window</p>
              <select
                value={filters.days}
                onChange={e => setFilter({ days: Number(e.target.value) })}
                className="text-xs px-2 py-1 border border-slate-200 rounded bg-white"
              >
                {[7, 30, 90, 180].map(d => <option key={d} value={d}>{d} days</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') setFilter({ search: searchInput }) }}
                onBlur={() => setFilter({ search: searchInput })}
                placeholder="Order #, tracking, city, postcode, store…"
                className="w-full text-xs pl-7 pr-2 py-1.5 border border-slate-200 rounded"
              />
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={() => { setSearchInput(''); setFilter({ carrier: [], state: [], international: undefined, search: '' }) }}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 transition-colors"
              >
                <X size={11} /> Clear ({activeFilterCount})
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={() => qc.invalidateQueries({ queryKey: ['tracking', 'exceptions'] })}
              disabled={isFetching}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 disabled:opacity-50"
            >
              {isFetching ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Refresh
            </button>
          </div>
        </div>

        {/* Count + paging */}
        <div className="flex items-center justify-between text-xs text-slate-500 mb-2 px-1">
          <p>
            {isLoading
              ? 'Loading…'
              : <>Showing <span className="font-medium text-slate-700">{rows.length}</span> of <span className="font-medium text-slate-700">{total}</span> exception orders</>}
          </p>
          {pageCount > 1 && (
            <div className="inline-flex items-center gap-1">
              <button
                onClick={() => setFilter({ page: filters.page - 1 })}
                disabled={filters.page <= 1}
                className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-slate-500 px-1">{filters.page} / {pageCount}</span>
              <button
                onClick={() => setFilter({ page: filters.page + 1 })}
                disabled={filters.page >= pageCount}
                className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Order</th>
                  <th className="px-3 py-2 text-left font-semibold">Client</th>
                  <th className="px-3 py-2 text-left font-semibold">Carrier</th>
                  <th className="px-3 py-2 text-left font-semibold">Tracking</th>
                  <th className="px-3 py-2 text-left font-semibold">Exception</th>
                  <th className="px-3 py-2 text-left font-semibold">Where</th>
                  <th className="px-3 py-2 text-left font-semibold">When</th>
                  <th className="px-3 py-2 text-left font-semibold">Destination</th>
                  <th className="px-3 py-2 text-right font-semibold">Shipped</th>
                  <th className="px-3 py-2 text-right font-semibold">In transit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 10 }).map((_, j) => (
                        <td key={j} className="px-3 py-2"><div className="skeleton h-3 rounded" style={{ width: '70px' }} /></td>
                      ))}
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-3 py-12 text-center text-slate-400">
                      <OctagonAlert size={20} className="mx-auto text-slate-300 mb-2" />
                      <p>Nothing flagged.</p>
                      <p className="text-[11px] text-slate-300 mt-1">
                        Either nothing's gone wrong (lucky) or the filters are excluding everything.
                      </p>
                    </td>
                  </tr>
                ) : rows.map(r => (
                  <tr
                    key={r.order_id}
                    onClick={() => setDrawerRow(r)}
                    className="hover:bg-red-50/40 cursor-pointer transition-colors"
                  >
                    <td className="px-3 py-2 font-medium text-slate-700">{r.order_number || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">
                      <div className="leading-tight">
                        <p>{r.client_name || '—'}</p>
                        {r.store_name && <p className="text-[10px] text-slate-400">{r.store_name}</p>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.carrier || '—'}</td>
                    <td className="px-3 py-2 font-mono text-[10.5px] text-slate-600">{r.tracking_number || '—'}</td>
                    <td className="px-3 py-2 text-red-700">
                      <span className="inline-flex items-center gap-1">
                        <OctagonAlert size={11} className="text-red-500" />
                        {r.exception_type || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-[11px]">
                      {r.exception_location || '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{timeAgo(r.exception_at)}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {r.recipient_suburb ? `${r.recipient_suburb}, ${r.recipient_state}` : r.recipient_state || '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{fmtDate(r.shipped_date)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.days_in_transit > 7 ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                      {r.days_in_transit != null ? `${r.days_in_transit}d` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Breakdown sidebar */}
      <aside className="card p-4 self-start">
        <h3 className="text-sm font-semibold text-slate-700 mb-1">Breakdown</h3>
        <p className="text-[11px] text-slate-400 mb-3">By exception type × carrier across all matching orders.</p>
        {breakdown.length === 0 ? (
          <p className="text-xs text-slate-400 italic py-4 text-center">Nothing to break down.</p>
        ) : (
          <ul className="space-y-1.5 max-h-[480px] overflow-y-auto pr-1">
            {breakdown.map((b, i) => (
              <li key={i} className="flex items-start justify-between gap-2 text-xs">
                <div className="min-w-0 flex-1">
                  <p className="text-slate-700 capitalize truncate" title={b.exception_type}>{b.exception_type}</p>
                  <p className="text-[10px] text-slate-400">{b.carrier}</p>
                </div>
                <span className="text-slate-500 font-medium tabular-nums flex-shrink-0">{b.count}</span>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* Drawer */}
      <OrderDetailDrawer row={drawerRow} onClose={() => setDrawerRow(null)} />
    </div>
  )
}
